import { useMemo } from "react";
import { interpolateText, type TemplateContext } from "@tellescope/utils";
import type { Enduser } from "@tellescope/types-models";
import type { TellescopeModelFromName } from "@tellescope/tanstack-query";
import type { LocalTellescopeFormResponseMap } from "./use-tellescope-form-core";
import { resolveFormTranslation, type FormTranslationMap } from "../../utils/resolve-form-translation";

export interface UseInterpolateFieldTextArgs {
  enduser: TellescopeModelFromName<"endusers"> | Enduser | undefined;
  currentResponses: LocalTellescopeFormResponseMap;
  /** The form's configured translations for the active language; omitted to render authored English. */
  translations?: FormTranslationMap;
}

export interface InterpolateFieldText {
  /** For text React renders as-is, where React's own escaping applies. */
  text: (value: string | undefined) => string | undefined;
  /** For text spliced into HTML before sanitization — substituted values are entity-escaped. */
  html: (value: string | undefined) => string | undefined;
}

/** Renders authored text verbatim. For surfaces with no enduser context to substitute from. */
export const PASS_THROUGH_FIELD_TEXT: InterpolateFieldText = {
  text: (value) => value,
  html: (value) => value,
};

/**
 * Substitutes `{{enduser.*}}` template values into a field's authored title/description, so a form
 * can address the enduser by name or echo a value they already gave. Answers from the form in
 * progress take precedence over the enduser record, so computed tokens (`BMI`, `Age`) reflect what
 * was just entered rather than a stale record.
 *
 * Also resolves the form's configured translation for the active language, when one is loaded. That
 * lookup runs *before* interpolation, because the map is keyed by the authored template
 * (`"Hello {{enduser.fname}}"`) — interpolating first would produce a string no key matches.
 *
 * `undefined` in, `undefined` out — callers pass optional field text straight through.
 *
 * This is the display half only. The same substitution is persisted onto the submitted response by
 * `substituteResponseFieldText` at the save/submit boundary, without the HTML escaping applied here.
 */
export const useInterpolateFieldText = ({
  enduser,
  currentResponses,
  translations,
}: UseInterpolateFieldTextArgs): InterpolateFieldText => {
  const context = useMemo(
    (): TemplateContext => ({
      enduser: enduser ?? null,
      organization: null,
      snippets: {},
      responses: Object.values(currentResponses),
    }),
    [enduser, currentResponses]
  );

  return useMemo(
    () => ({
      text: (value: string | undefined) => {
        const translated = resolveFormTranslation(value, translations);
        return translated === undefined ? undefined : interpolateText(translated, context);
      },
      html: (value: string | undefined) => {
        const translated = resolveFormTranslation(value, translations);
        return translated === undefined ? undefined : interpolateText(translated, context, { escapeValues: true });
      },
    }),
    [context, translations]
  );
};
